<div
  class="cmdk overlay"
  [class.is-open]="open()"
  [@backdrop]="open() ? 'open' : 'closed'"
  (@backdrop.done)="onBackdropAnimationDone($event)"
  (click)="onBackdropClick($event)"
  role="presentation"
>
  <div
    class="panel-anim"
    [class.is-open]="open()"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="'company-tool.cmdk.aria.title' | transloco"
    [attr.aria-hidden]="open() ? null : 'true'"
    [attr.inert]="open() ? null : ''"
    (click)="$event.stopPropagation()"
  >
    <div class="panel ct-card">
      <cmdk-command class="command" [filter]="cmdkFilter">
        <div class="search">
          <fa-icon
            class="search-icon"
            [icon]="fontawesomeService.light.search"
            aria-hidden="true"
          ></fa-icon>
          <input
            #searchInput
            cmdkInput
            class="input"
            [attr.placeholder]="
              'company-tool.cmdk.search.placeholder' | transloco
            "
            [attr.aria-label]="'company-tool.cmdk.search.aria' | transloco"
            [value]="query()"
            (input)="onQueryInput($event)"
            autocomplete="off"
            autocapitalize="off"
            spellcheck="false"
          />
        </div>

        <cmdk-list class="list" [attr.aria-label]="'Suggestions'">
          <div *cmdkEmpty class="empty">
            {{ 'company-tool.cmdk.empty' | transloco }}
          </div>

          <app-animated-height-container [hideOverflow]="false">
            <div class="list-inner">
              @if (queryTrimmed().length > 0 && hasAnyResults()) {
                <cmdk-group
                  [label]="'company-tool.cmdk.groups.search' | transloco"
                  class="group"
                >
                  <button
                    cmdkItem
                    type="button"
                    class="item"
                    [value]="
                      ('company-tool.cmdk.actions.search.show-all'
                        | transloco) +
                      ' ' +
                      query()
                    "
                    (selected)="onShowAllResults()"
                  >
                    <span class="left">
                      <fa-icon
                        [icon]="fontawesomeService.light.search"
                        aria-hidden="true"
                        class="item-icon"
                      ></fa-icon>
                      <span class="label">
                        {{
                          'company-tool.cmdk.actions.search.show-all'
                            | transloco
                        }}
                      </span>
                    </span>
                  </button>
                </cmdk-group>
              }

              <cmdk-group
                [label]="'company-tool.cmdk.groups.jobs' | transloco"
                class="group"
              >
                @if (queryTrimmed().length > 0 && searching()) {
                  <button
                    cmdkItem
                    type="button"
                    class="item skeleton-item"
                    disabled
                  >
                    <span class="left" style="width: 100%">
                      <ngx-skeleton-loader
                        [count]="1"
                        appearance="line"
                        [theme]="skeletonLoaderTheme"
                        [animation]="'progress'"
                      ></ngx-skeleton-loader>
                    </span>
                  </button>
                }
                @for (item of filteredStaticJobs(); track item.key) {
                  <button
                    cmdkItem
                    type="button"
                    class="item"
                    [value]="item.key | transloco"
                    (selected)="onStaticAction(item)"
                  >
                    <span class="left">
                      <fa-icon
                        [icon]="item.icon"
                        aria-hidden="true"
                        class="item-icon"
                      ></fa-icon>
                      <span class="label">{{ item.key | transloco }}</span>
                    </span>
                  </button>
                }

                @for (item of jobResults(); track item.id) {
                  <button
                    cmdkItem
                    type="button"
                    class="item"
                    [value]="item.title ?? ''"
                    (selected)="onNavigateToResult(item)"
                  >
                    <span class="left">
                      <fa-icon
                        [icon]="fontawesomeService.light.listAlt"
                        aria-hidden="true"
                        class="item-icon"
                      ></fa-icon>
                      <span class="label">{{
                        item.title ?? 'Job #' + item.id
                      }}</span>
                    </span>
                  </button>
                }

                @if (
                  showEmptyCategoryHint() &&
                  !searching() &&
                  filteredStaticJobs().length === 0 &&
                  jobResults().length === 0
                ) {
                  <button cmdkItem type="button" class="item" disabled>
                    <span class="left">
                      <span class="label">{{
                        'company-tool.cmdk.empty-category' | transloco
                      }}</span>
                    </span>
                  </button>
                }
              </cmdk-group>

              <cmdk-group
                [label]="'company-tool.cmdk.groups.applications' | transloco"
                class="group"
              >
                @if (queryTrimmed().length > 0 && searching()) {
                  <button
                    cmdkItem
                    type="button"
                    class="item skeleton-item"
                    disabled
                  >
                    <span class="left" style="width: 100%">
                      <ngx-skeleton-loader
                        [count]="1"
                        appearance="line"
                        [theme]="skeletonLoaderTheme"
                        [animation]="'progress'"
                      ></ngx-skeleton-loader>
                    </span>
                  </button>
                }
                @for (item of filteredStaticApplications(); track item.key) {
                  <button
                    cmdkItem
                    type="button"
                    class="item"
                    [value]="item.key | transloco"
                    (selected)="onStaticAction(item)"
                  >
                    <span class="left">
                      <fa-icon
                        [icon]="item.icon"
                        aria-hidden="true"
                        class="item-icon"
                      ></fa-icon>
                      <span class="label">{{ item.key | transloco }}</span>
                    </span>
                  </button>
                }

                @for (item of applicationResults(); track item.id) {
                  <button
                    cmdkItem
                    type="button"
                    class="item"
                    [value]="
                      (item.firstName ?? '') +
                      ' ' +
                      (item.lastName ?? '') +
                      ' ' +
                      (item.jobTitle ?? '')
                    "
                    (selected)="onNavigateToResult(item)"
                  >
                    <span class="left">
                      <span class="label">
                        {{
                          (item.firstName ?? '') + ' ' + (item.lastName ?? '')
                        }}
                        @if (item.jobTitle) {
                          • <i>{{ item.jobTitle }}</i>
                        }
                      </span>
                    </span>
                  </button>
                }

                @if (
                  showEmptyCategoryHint() &&
                  !searching() &&
                  filteredStaticApplications().length === 0 &&
                  applicationResults().length === 0
                ) {
                  <button cmdkItem type="button" class="item" disabled>
                    <span class="left">
                      <span class="label">{{
                        'company-tool.cmdk.empty-category' | transloco
                      }}</span>
                    </span>
                  </button>
                }
              </cmdk-group>

              <cmdk-group
                [label]="'company-tool.cmdk.groups.functions' | transloco"
                class="group"
              >
                @if (queryTrimmed().length > 0 && searching()) {
                  <button
                    cmdkItem
                    type="button"
                    class="item skeleton-item"
                    disabled
                  >
                    <span class="left" style="width: 100%">
                      <ngx-skeleton-loader
                        [count]="1"
                        appearance="line"
                        [theme]="skeletonLoaderTheme"
                        [animation]="'progress'"
                      ></ngx-skeleton-loader>
                    </span>
                  </button>
                }
                @for (item of filteredStaticFunctions(); track item.key) {
                  <button
                    cmdkItem
                    type="button"
                    class="item"
                    [value]="item.key | transloco"
                    (selected)="onStaticAction(item)"
                  >
                    <span class="left">
                      <fa-icon
                        [icon]="item.icon"
                        aria-hidden="true"
                        class="item-icon"
                      ></fa-icon>
                      <span class="label">{{ item.key | transloco }}</span>
                    </span>
                  </button>
                }

                @for (
                  item of functionResults();
                  track item.path ?? item.label ?? ''
                ) {
                  <button
                    cmdkItem
                    type="button"
                    class="item"
                    [value]="item.label ?? ''"
                    (selected)="onNavigateToResult(item)"
                  >
                    <span class="left">
                      <fa-icon
                        [icon]="fontawesomeService.light.tv"
                        aria-hidden="true"
                        class="item-icon"
                      ></fa-icon>
                      <span class="label">{{
                        item.label ??
                          ('company-tool.cmdk.fallback.function' | transloco)
                      }}</span>
                    </span>
                  </button>
                }

                @if (
                  showEmptyCategoryHint() &&
                  !searching() &&
                  filteredStaticFunctions().length === 0 &&
                  functionResults().length === 0
                ) {
                  <button cmdkItem type="button" class="item" disabled>
                    <span class="left">
                      <span class="label">{{
                        'company-tool.cmdk.empty-category' | transloco
                      }}</span>
                    </span>
                  </button>
                }
              </cmdk-group>

              @if (queryTrimmed().length > 0 && !searching()) {
                <cmdk-group
                  [label]="'company-tool.cmdk.groups.kira' | transloco"
                  class="group kira-group"
                >
                  <button
                    type="button"
                    cmdkItem
                    class="item kira kira-gradient-border"
                    [value]="'__cmdk-kira-send__'"
                    [attr.aria-label]="
                      'company-tool.cmdk.kira.send-aria'
                        | transloco: { query: query() }
                    "
                    (selected)="onKiraAsk()"
                  >
                    <span class="left">
                      <fa-icon
                        [icon]="fontawesomeService.light.sparkles"
                        aria-hidden="true"
                        class="item-icon"
                      ></fa-icon>
                      <span class="label kira-line">
                        {{ 'company-tool.cmdk.kira.send' | transloco }}: "{{
                          query()
                        }}"
                      </span>
                    </span>
                  </button>
                </cmdk-group>
              }
            </div>
          </app-animated-height-container>
        </cmdk-list>

        <div class="footer">
          <div class="footer-left"></div>

          <div class="footer-right">
            <app-kbd keys="Esc"></app-kbd>
            <span class="footer-text">{{
              'company-tool.cmdk.footer.close' | transloco
            }}</span>
          </div>
        </div>
      </cmdk-command>
    </div>
  </div>
</div>
